import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getArchivedTeacherAssignments, getTeacherAssignments, TeacherAssignment } from '../api/AssignmentTeacherApi';
import ModuleContext from '../../../_contexts/ModuleContext';
import useThrowAsyncError from '../../../../../hooks/useThrowAsyncError';
import useStudioGroup from '../../../../../hooks/useStudioGroup';
import useSchoolYearValues, { SchoolYearValue, SchoolYearValues } from '../../../_hooks/useSchoolYearValues';

type TeacherAssignmentsContextValue = {
  assignments: TeacherAssignment[];
  fetchAssignments: () => Promise<void>;
  isLoading: boolean;
  archived: boolean;
  schoolYears: SchoolYearValues | undefined;
  selectedSchoolYear: SchoolYearValue | undefined;
  setSelectedSchoolYear: (schoolYear: SchoolYearValue) => void;
};

const defaultTeacherAssignmentsContextValue: TeacherAssignmentsContextValue = {
  assignments: [],
  fetchAssignments: () => Promise.resolve(),
  isLoading: false,
  archived: false,
  schoolYears: undefined,
  selectedSchoolYear: undefined,
  setSelectedSchoolYear: () => {},
};

const TeacherAssignmentsContext = createContext<TeacherAssignmentsContextValue>(defaultTeacherAssignmentsContextValue);

export default TeacherAssignmentsContext;

export const TeacherAssignmentsContextProvider = ({
  children,
  archived,
  schoolYear,
  onSchoolYearChange,
}: {
  children: ReactNode;
  archived: boolean;
  schoolYear?: SchoolYearValue;
  onSchoolYearChange?: (schoolYear: SchoolYearValue) => void;
}): JSX.Element => {
  const studioGroup = useStudioGroup();
  const [assignments, setAssignments] = useState<TeacherAssignment[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleError = useThrowAsyncError();
  const { module } = useContext(ModuleContext);
  const hasDigitalManualLicense = module?.licenses.hasDigitalManualLicenseForTeacher;
  const hasBaollLicense = module?.licenses.hasBaollLicense;

  const { schoolYears } = useSchoolYearValues();
  const [pickedSchoolYear, setPickedSchoolYear] = useState<SchoolYearValue>();
  const selectedSchoolYear = schoolYear ?? pickedSchoolYear ?? schoolYears?.current;
  const setSelectedSchoolYear = onSchoolYearChange ?? setPickedSchoolYear;

  const startYear = selectedSchoolYear?.from.getFullYear();
  const endYear = selectedSchoolYear?.to.getFullYear();

  const fetchAssignments = useCallback(async () => {
    if (!module || !hasDigitalManualLicense || (studioGroup === 'BAO' && !hasBaollLicense)) {
      setAssignments([]);
      return;
    }

    setIsLoading(true);

    try {
      const fetchedAssignments = archived
        ? await getArchivedTeacherAssignments(module.id, startYear && endYear ? { startYear, endYear } : undefined)
        : await getTeacherAssignments(module.id);
      setAssignments(fetchedAssignments);
    } catch (err) {
      handleError(err);
    } finally {
      setIsLoading(false);
    }
  }, [module, hasDigitalManualLicense, studioGroup, hasBaollLicense, archived, handleError, startYear, endYear]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  const teacherAssignmentsContextValue = useMemo(
    () => ({
      assignments,
      fetchAssignments,
      isLoading,
      archived,
      schoolYears,
      selectedSchoolYear,
      setSelectedSchoolYear,
    }),
    [assignments, fetchAssignments, isLoading, archived, schoolYears, selectedSchoolYear, setSelectedSchoolYear],
  );

  return (
    <TeacherAssignmentsContext.Provider value={teacherAssignmentsContextValue}>
      {children}
    </TeacherAssignmentsContext.Provider>
  );
};
